import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Checkbox } from '@/kit/Checkbox';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { DateField, type DateValue } from '@/kit/DateField';
import { InputField } from '@/kit/FormField';
import { NumberBox } from './NumberBox';
import { ConfirmPopup, type ConfirmPopupData } from '@/kit/ConfirmPopup';
import { openDialog } from '@/kit/dialog';
import { sameValues } from '@/kit/hooks/useDirtyForm';
import { Icon } from '@/kit/Icon';
import { LeaveGuard } from '@/kit/LeaveGuard';
import { Radio, RadioGroup } from '@/kit/Radio';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { Select } from '@/kit/Select';
import { NotificationTextMessage } from '@/core/enums/notification';
import { periodRangeText } from '@/core/format/periodLabel';
import type {
  CicAssetTransferRow,
  CicBorrowingRow,
  CicReportContent,
  CicReportView,
} from '@/core/models/cic-report.models';
import type { RichTextConfig } from '@/core/models/richText';
import { toIsoDate, type PeriodRow } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { cicReportService } from '@/core/services/cicReport';
import { practiceService } from '@/core/services/practice';
import { reportsService } from '@/core/services/reports';
import { SETTINGS_ROUTE, useReportClient } from './common';
import { StickyReportSettingsFrame } from './StickyReportSettingsFrame';
import './Cic34Report.scss';

const NO_PERIOD = 'Add an accounting period for this client before completing the CIC34 report.';

type EditorField =
  | 'activitiesAndImpact'
  | 'stakeholderNames'
  | 'consultationMethod'
  | 'consultationOutcome'
  | 'remunerationDetails'
  | 'assetTransferDetails'
  | 'performanceInterestDetails';
type AnswerField =
  | 'consultationHeld'
  | 'remunerationReceived'
  | 'remunerationInAccounts'
  | 'assetTransfersMade'
  | 'performanceInterestPaid';
type NumberField = 'directorsEmoluments' | 'thirdPartyPayments' | 'moneyPurchaseDirectors' | 'definedBenefitDirectors';

const emptyReport = (): CicReportContent => ({
  activitiesAndImpact: null,
  consultationHeld: null,
  stakeholderNames: null,
  consultationMethod: null,
  consultationOutcome: null,
  remunerationReceived: null,
  remunerationInAccounts: null,
  directorsEmoluments: null,
  thirdPartyPayments: null,
  moneyPurchaseDirectors: null,
  definedBenefitDirectors: null,
  remunerationDetails: null,
  remunerationTitle: null,
  assetTransfersMade: null,
  assetTransferDetails: null,
  assetTransfers: [],
  dividendsPaid: null,
  dividendDetails: null,
  performanceInterestPaid: null,
  performanceInterestDetails: null,
  borrowings: [],
  signingDirectorId: null,
  signingDirectorName: null,
  signingDate: null,
  excludedParts: [],
});

/** Angular `Cic34ReportComponent.editor(editable)`. */
export function cicEditorConfig(editable: boolean): RichTextConfig {
  return {
    editable,
    spellcheck: true,
    height: '140',
    minHeight: '0',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: editable,
    showToolbar: editable,
    placeholder: 'Enter text here...',
    defaultParagraphSeparator: '',
    sanitize: true,
    toolbarPosition: 'bottom',
    toolbarHiddenButtons: [
      [
        'fontName',
        'fontSize',
        'backgroundColor',
        'strikeThrough',
        'customClasses',
        'insertHorizontalRule',
        'removeFormat',
        'heading',
        'insertImage',
        'insertVideo',
      ],
    ],
  };
}

const editorConfig = cicEditorConfig(true);
const readOnlyEditorConfig = cicEditorConfig(false);

/** Angular's private `number(v)`: blank or non-numeric becomes null. */
export function cicNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The words in rich text, without its tags. */
const plainText = (html: string | null | undefined) =>
  (html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const periodLabel = (period: PeriodRow) =>
  periodRangeText(period.periodFrom ?? period.fromDate, period.periodTo ?? period.toDate, ' - ');

/**
 * cap3-f: the CIC34 community interest company report, captured per accounting period for clients that are Community
 * Interest Companies. Read-only for locked periods and non-CIC clients. Always editable otherwise (3 Nov): Save and Discard
 * are in the sticky page header, shown once an answer differs from what was loaded or saved, so Save is never at the
 * foot of a long page.
 */
export function Cic34Report() {
  const navigate = useNavigate();
  const client = useReportClient();
  const [isSaving, setIsSaving] = useState(false);
  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [view, setView] = useState<CicReportView | null>(null);
  const [report, setReport] = useState<CicReportContent>(emptyReport);
  /** The datepicker value; converted to yyyy-MM-dd on save. */
  const [signingDate, setSigningDate] = useState<DateValue>(null);
  /** The answers as last loaded or saved: what the page is compared with, and what Discard puts back. */
  const [baseline, setBaseline] = useState<{ report: CicReportContent; signingDate: string | null } | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const readOnly = !view || view.isReadOnly || !view.isCommunityInterest;
  const config = readOnly ? readOnlyEditorConfig : editorConfig;

  const apply = (loaded: CicReportView) => {
    const shown: CicReportContent = {
      ...emptyReport(),
      ...loaded.report,
      assetTransfers: [...(loaded.report?.assetTransfers ?? [])],
      borrowings: [...(loaded.report?.borrowings ?? [])],
    };
    setView(loaded);
    setReport(shown);
    setSigningDate(loaded.report?.signingDate ?? null);
    setBaseline({ report: shown, signingDate: toIsoDate(loaded.report?.signingDate ?? null) ?? null });
  };

  const dirty =
    !readOnly &&
    !!baseline &&
    !sameValues(
      { report, signingDate: toIsoDate(signingDate) ?? null },
      { report: baseline.report, signingDate: baseline.signingDate },
    );

  const discard = () => {
    if (!baseline || isSaving) return;
    setReport(baseline.report);
    setSigningDate(baseline.signingDate);
  };

  const load = (id: string) => {
    if (!client) return;
    cicReportService.get(client.id, id).then(
      (loaded) => alive.current && apply(loaded),
      (error) => alive.current && notifyError(error),
    );
  };

  useEffect(() => {
    if (!client) return;
    Promise.all([
      practiceService.getClientPeriods(client.id),
      // A supplementary lookup: without the reports the page still opens, on the latest period that has started.
      reportsService.getReports(client.id, 0).catch(() => []),
    ]).then(
      ([response, reports]) => {
        if (!alive.current) return;
        const sorted = [...(response.periods ?? [])]
          .filter((p) => !p.isComparative)
          .sort((a, b) => `${b.periodTo ?? ''}`.localeCompare(`${a.periodTo ?? ''}`));
        setPeriods(sorted);
        const current = defaultCicPeriod(sorted, reports ?? []);
        if (!current) {
          notifyError(NO_PERIOD);
          return;
        }
        setPeriodId(current.id);
        load(current.id);
      },
      (error) => alive.current && notifyError(error),
    );
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPeriodChange = async (id: string) => {
    // Another period shows its own answers, so changes not yet saved would be lost: ask first.
    if (dirty) {
      const leave = await openDialog<ConfirmPopupData, boolean>(ConfirmPopup, {
        data: { title: 'Unsaved changes', message: 'Switch period? Your unsaved changes will be lost.' },
      }).afterClosed();
      if (leave !== true || !alive.current) return;
    }
    setPeriodId(id);
    load(id);
  };

  const change = (patch: Partial<CicReportContent>) => setReport((current) => ({ ...current, ...patch }));

  /** BUG-063: a part can be left out of the printed CIC34 report. */
  const excluded = (code: string) => (report.excludedParts ?? []).includes(code);
  const setIncluded = (code: string, included: boolean) =>
    change({
      excludedParts: included
        ? (report.excludedParts ?? []).filter((c) => c !== code)
        : [...(report.excludedParts ?? []).filter((c) => c !== code), code],
    });
  const partHeader = (code: string, title: ReactNode) => (
    <div className="cic-part-head d-flex align-items-center justify-space-between">
      <h3>{title}</h3>
      <Checkbox
        name={`include-${code}`}
        checked={!excluded(code)}
        disabled={readOnly}
        onChange={(event) => setIncluded(code, event.target.checked)}
      >
        Include in report
      </Checkbox>
    </div>
  );
  const partClass = (code: string, extra = '') => `cic-part${extra}${excluded(code) ? ' cic-part-excluded' : ''}`;

  const assetsOff = readOnly || report.assetTransfersMade === false;
  const addAsset = () =>
    setReport((current) => ({
      ...current,
      assetTransfers: [...current.assetTransfers, { description: '', estimatedValue: 0 }],
    }));
  const removeAsset = (index: number) =>
    setReport((current) => ({ ...current, assetTransfers: current.assetTransfers.filter((_, i) => i !== index) }));
  const changeAsset = (index: number, patch: Partial<CicAssetTransferRow>) =>
    setReport((current) => ({
      ...current,
      assetTransfers: current.assetTransfers.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  const addBorrowing = () =>
    setReport((current) => ({
      ...current,
      borrowings: [
        ...current.borrowings,
        { description: '', effectiveInterestRate: 0, interestCapRate: null, whyCapDoesNotApply: null },
      ],
    }));
  const removeBorrowing = (index: number) =>
    setReport((current) => ({ ...current, borrowings: current.borrowings.filter((_, i) => i !== index) }));
  const changeBorrowing = (index: number, patch: Partial<CicBorrowingRow>) =>
    setReport((current) => ({
      ...current,
      borrowings: current.borrowings.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  /**
   * BUG-063: the signatory is a director of the company. A name saved before the picker that matches no director stays as a
   * read-only legacy value until a director is chosen.
   */
  const legacySigner = view && !view.report.signingDirectorId ? (view.report.signingDirectorName ?? '').trim() : '';
  const onSigningDirectorChange = (directorId: string | null) => {
    const director = view?.directors.find((d) => d.id === directorId);
    change(
      director
        ? { signingDirectorId: director.id, signingDirectorName: director.name }
        : { signingDirectorId: null, signingDirectorName: legacySigner || null },
    );
  };

  const save = () => {
    if (!view || readOnly || isSaving) return;
    const payload: CicReportContent = {
      ...report,
      // The tick is a yes/no once remuneration was received: left unticked it says the details are given here.
      remunerationInAccounts: report.remunerationReceived === true ? report.remunerationInAccounts === true : null,
      directorsEmoluments: cicNumber(report.directorsEmoluments),
      thirdPartyPayments: cicNumber(report.thirdPartyPayments),
      moneyPurchaseDirectors: cicNumber(report.moneyPurchaseDirectors),
      definedBenefitDirectors: cicNumber(report.definedBenefitDirectors),
      assetTransfers: report.assetTransfers.map((a) => ({
        description: a.description ?? '',
        estimatedValue: cicNumber(a.estimatedValue) ?? 0,
      })),
      borrowings: report.borrowings.map((b) => ({
        description: b.description ?? '',
        effectiveInterestRate: cicNumber(b.effectiveInterestRate) ?? 0,
        interestCapRate: cicNumber(b.interestCapRate),
        whyCapDoesNotApply: b.whyCapDoesNotApply || null,
      })),
      signingDate: toIsoDate(signingDate),
    };
    setIsSaving(true);
    cicReportService
      .save({ clientId: view.clientId, accountingPeriodId: view.accountingPeriodId, report: payload })
      .then(
        (saved) => {
          if (!alive.current) return;
          apply(saved);
          notifySuccess(NotificationTextMessage.successMessage);
        },
        (error) => alive.current && notifyError(error),
      )
      .finally(() => alive.current && setIsSaving(false));
  };

  const editor = (field: EditorField) => (
    <RichTextEditor value={report[field]} onChange={(html) => change({ [field]: html })} config={config} />
  );

  const answer = (field: AnswerField, yes = 'Yes', no = 'No') => (
    <RadioGroup
      name={field}
      value={report[field]}
      disabled={readOnly}
      onChange={(value) => change({ [field]: value as boolean })}
    >
      <Radio value={true} hostClassName="example-radio-button">
        {yes}
      </Radio>
      <Radio value={false} hostClassName="example-radio-button">
        {no}
      </Radio>
    </RadioGroup>
  );

  const numberBox = (field: NumberField, label: string, step?: number) => (
    <div className="form-field">
      <p>{label}</p>
      <NumberBox
        fieldClassName="w-200"
        maxIntDigits={step === 1 ? 6 : 15}
        maxDecimals={step === 1 ? 0 : 4}
        name={field}
        disabled={readOnly}
        value={report[field]}
        onValue={(value) => change({ [field]: value })}
      />
    </div>
  );

  const question = (label: string, content: ReactNode) => (
    <div className="form-field">
      <p>{label}</p>
      {content}
    </div>
  );

  return (
    <StickyReportSettingsFrame
      title="CIC34 Report"
      hostClassName="cic34-report-host"
      onClose={() => navigate(SETTINGS_ROUTE)}
      dirty={dirty}
      actionsPlacement="footer"
      busy={isSaving}
      onSave={save}
      onDiscard={discard}
    >
      <LeaveGuard when={dirty} />
      <div className="sidenav-body">
        <div className="form-field w-300">
          <p>Accounting Period</p>
          <Select
            className="w-300"
            clearable={false}
            items={periods.map((period) => ({ id: period.id, label: periodLabel(period) }))}
            bindValue="id"
            bindLabel="label"
            value={periodId}
            onChange={(value) => void onPeriodChange(value as string)}
          />
        </div>

        {view && (
          <>
            {!view.isCommunityInterest && (
              <p className="text-red mt-10">
                This client is not a Community Interest Company. Tick "Company is a Community Interest Company" on the
                client record to complete the CIC34 report.
              </p>
            )}
            {view.isCommunityInterest && view.isReadOnly && (
              <p className="mt-10">
                This accounting period is finalised, locked or out for client approval, so its CIC34 report is
                read-only.
              </p>
            )}

            {view.isCommunityInterest && view.missingParts.length > 0 && (
              <div className="cic-missing mt-10">
                <p className="fw-bold">Still required before the accounts can be approved or filed</p>
                <ul>
                  {view.missingParts.map((part, index) => (
                    <li key={index}>{part.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Part 1 */}
            <div className={partClass('PART1', ' mt-10')}>
              {partHeader('PART1', "Part 1 - General description of the company's activities and impact")}
              {question(
                // TC 86408: 3.0's instruction; there is no default content, so Part 1 left empty is not printed.
                "Please insert a general account of the company's activities in the financial year to which the report relates, including a description of how they have benefited the community.",
                editor('activitiesAndImpact'),
              )}
            </div>

            {/* Part 2 */}
            <div className={partClass('PART2')}>
              {partHeader('PART2', 'Part 2 - Consultation with stakeholders')}
              {question('Has the company consulted its stakeholders?', answer('consultationHeld'))}
              {report.consultationHeld === true && (
                <>
                  {question("Who are the company's stakeholders?", editor('stakeholderNames'))}
                  {question('How have the stakeholders been consulted?', editor('consultationMethod'))}
                  {question(
                    'What action, if any, has the company taken in response to the consultation?',
                    editor('consultationOutcome'),
                  )}
                </>
              )}
            </div>

            {/* Part 3 */}
            <div className={partClass('PART3')}>
              {partHeader('PART3', "Part 3 - Directors' remuneration")}
              {question('Was remuneration received by the directors?', answer('remunerationReceived'))}
              {/* BUG-063: as 3.0 (applyPart3State), the "full details are in the accounts" tick is always shown; the answer No leaves it disabled and unticked, and ticking it means remuneration was received. */}
              <div className="form-field">
                <Checkbox
                  name="remunerationInAccounts"
                  checked={report.remunerationReceived !== false && report.remunerationInAccounts === true}
                  disabled={readOnly || report.remunerationReceived === false}
                  onChange={(event) =>
                    change({
                      remunerationInAccounts: event.target.checked,
                      ...(event.target.checked ? { remunerationReceived: true } : {}),
                    })
                  }
                >
                  Tick if you have provided full details of the directors’ remuneration in the accounts: you then do not
                  need to disclose it here, but you must explain in the space below where they are found within the
                  accounts.
                </Checkbox>
              </div>
              {report.remunerationReceived === true && (
                <>
                  {report.remunerationInAccounts === true ? (
                    question(
                      "Explain where the directors' remuneration is found within the accounts",
                      editor('remunerationDetails'),
                    )
                  ) : (
                    <>
                      <div className="form-field">
                        <p>Title</p>
                        <InputField
                          fieldClassName="w-300"
                          maxLength={200}
                          name="remunerationTitle"
                          charMode="text"
                          placeholder="Director's Emoluments"
                          disabled={readOnly}
                          value={report.remunerationTitle ?? ''}
                          onChange={(event) => change({ remunerationTitle: event.target.value })}
                        />
                      </div>
                      <div className="cic-row">
                        {numberBox('directorsEmoluments', 'Emoluments (£)')}
                        {numberBox('thirdPartyPayments', "Paid to third parties for directors' services (£)")}
                        {numberBox('moneyPurchaseDirectors', 'Directors in money purchase schemes', 1)}
                        {numberBox('definedBenefitDirectors', 'Directors in defined benefit schemes', 1)}
                      </div>
                      {question(
                        "Enter additional text to appear within the directors' remuneration note",
                        editor('remunerationDetails'),
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Part 4 */}
            <div className={partClass('PART4')}>
              {partHeader('PART4', 'Part 4 - Transfers of assets other than for full consideration')}
              {question(
                'Have any transfers of assets other than for full consideration been made?',
                answer('assetTransfersMade'),
              )}
              {/* BUG-063: as 3.0 (Part4content), the asset table is always shown; the answer No leaves it disabled. */}
              <table className="cic-table" aria-label="Transfers of assets">
                <thead>
                  <tr>
                    <th>Description of Asset</th>
                    <th>Estimated Value</th>
                    <th className="cic-table-action">
                      <Button className="light-bg" disabled={assetsOff} onClick={addAsset}>
                        <Icon name="add" />
                      </Button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.assetTransfers.map((asset, i) => (
                    <tr key={i}>
                      <td>
                        <InputField
                          fieldClassName="w-300"
                          maxLength={1000}
                          name={`assetDescription${i}`}
                          disabled={assetsOff}
                          value={asset.description ?? ''}
                          onChange={(event) => changeAsset(i, { description: event.target.value })}
                        />
                      </td>
                      <td>
                        <NumberBox
                          fieldClassName="w-200"
                          maxIntDigits={15}
                          maxDecimals={4}
                          name={`assetValue${i}`}
                          disabled={assetsOff}
                          value={asset.estimatedValue}
                          onValue={(value) => changeAsset(i, { estimatedValue: value as number })}
                        />
                      </td>
                      <td className="cic-table-action">
                        <Button className="light-bg" title="Remove" disabled={assetsOff} onClick={() => removeAsset(i)}>
                          <Icon name="delete_outline" outlined />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {report.assetTransfersMade === true && (
                <>
                  {question(
                    'Enter the details below to be included within the transfer of assets other than for full consideration note (to an asset-locked body or for the benefit of the community)',
                    editor('assetTransferDetails'),
                  )}
                </>
              )}
            </div>

            {/* Part 5 */}
            <div className={partClass('PART5')}>
              {partHeader('PART5', 'Part 5 - Interest paid at a performance-related rate')}
              {question('Was any interest paid at a performance-related rate?', answer('performanceInterestPaid'))}
              {report.performanceInterestPaid === true && (
                <>
                  <table className="cic-table" aria-label="Interest paid at a performance-related rate">
                    <thead>
                      <tr>
                        <th>Description of borrowings</th>
                        <th>Effective interest rate %</th>
                        <th>Interest cap value %</th>
                        <th>Why interest cap does not apply</th>
                        <th className="cic-table-action">
                          <Button className="light-bg" disabled={readOnly} onClick={addBorrowing}>
                            <Icon name="add" />
                          </Button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.borrowings.map((borrowing, i) => (
                        <tr key={i}>
                          <td>
                            <InputField
                              fieldClassName="w-300"
                              maxLength={1000}
                              name={`borrowingDescription${i}`}
                              disabled={readOnly}
                              value={borrowing.description ?? ''}
                              onChange={(event) => changeBorrowing(i, { description: event.target.value })}
                            />
                          </td>
                          <td>
                            <NumberBox
                              fieldClassName="w-150"
                              max={100}
                              maxIntDigits={3}
                              maxDecimals={4}
                              name={`borrowingRate${i}`}
                              disabled={readOnly}
                              value={borrowing.effectiveInterestRate}
                              onValue={(value) => changeBorrowing(i, { effectiveInterestRate: value as number })}
                            />
                          </td>
                          <td>
                            <NumberBox
                              fieldClassName="w-150"
                              max={100}
                              maxIntDigits={3}
                              maxDecimals={4}
                              name={`borrowingCap${i}`}
                              disabled={readOnly}
                              value={borrowing.interestCapRate}
                              onValue={(value) => changeBorrowing(i, { interestCapRate: value })}
                            />
                          </td>
                          <td>
                            <InputField
                              fieldClassName="w-300"
                              maxLength={2000}
                              name={`borrowingWhy${i}`}
                              disabled={readOnly}
                              value={borrowing.whyCapDoesNotApply ?? ''}
                              onChange={(event) => changeBorrowing(i, { whyCapDoesNotApply: event.target.value })}
                            />
                          </td>
                          <td className="cic-table-action">
                            <Button
                              className="light-bg"
                              title="Remove"
                              disabled={readOnly}
                              onClick={() => removeBorrowing(i)}
                            >
                              <Icon name="delete_outline" outlined />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {question(
                    'Enter details of how the rate of interest and the interest cap were determined.',
                    editor('performanceInterestDetails'),
                  )}
                </>
              )}
              {report.dividendsPaid === true && (
                <div className="cic-legacy form-field">
                  <p>
                    Dividends were recorded in this part by an earlier version of the report. They are no longer asked
                    for here, but still print until removed.
                  </p>
                  {plainText(report.dividendDetails) && (
                    <p className="cic-legacy-text">{plainText(report.dividendDetails)}</p>
                  )}
                  <Button
                    className="light-bg"
                    disabled={readOnly}
                    onClick={() => change({ dividendsPaid: false, dividendDetails: null })}
                  >
                    Remove dividends
                  </Button>
                </div>
              )}
            </div>

            {/* Part 6: the signatory is one of the company's directors, with the date. */}
            <div className={partClass('SIGNING')}>
              {partHeader('SIGNING', 'Part 6 - Signatory')}
              <div className="cic-row">
                <div className="form-field">
                  <p>Director</p>
                  <Select
                    className="w-300"
                    disabled={readOnly}
                    items={view.directors}
                    bindLabel="name"
                    bindValue="id"
                    value={report.signingDirectorId}
                    onChange={(value) => onSigningDirectorChange(value as string | null)}
                  />
                </div>
                <div className="form-field">
                  <p>Date signed</p>
                  <DateField
                    fieldClassName="w-200"
                    readOnly
                    name="signingDate"
                    placeholder="DD/MM/YYYY"
                    disabled={readOnly}
                    value={signingDate}
                    onChange={setSigningDate}
                  />
                </div>
              </div>
              {view.directors.length === 0 && (
                <p className="mt-10">No directors are recorded for this period. Add them under Company Contacts.</p>
              )}
              {!report.signingDirectorId && legacySigner && (
                <p className="cic-legacy mt-10">
                  Saved before directors could be chosen: <strong>{legacySigner}</strong>. This name is kept until you
                  choose a director.
                </p>
              )}
            </div>

          </>
        )}
      </div>
    </StickyReportSettingsFrame>
  );
}

/**
 * Round 4 #127 (86424): the period the CIC34 page opens on. The CIC report prints the CIC34 report of its accounts' period, so the page
 * opens on the latest period that has annual accounts - not on a later period generated ahead (auto period generation), where answers
 * saved would never reach the accounts the user downloads. Without accounts: the latest period that has started, else the latest.
 */
export function defaultCicPeriod(
  periods: readonly PeriodRow[],
  reports: readonly { fromDate?: string | null; toDate?: string | null }[],
  today: string = toIsoDate(new Date()) ?? '',
): PeriodRow | undefined {
  const day = isoDay;
  const withAccounts = periods.find((p) =>
    reports.some((r) => day(r.fromDate) === day(p.periodFrom) && day(r.toDate) === day(p.periodTo)),
  );
  return withAccounts ?? periods.find((p) => day(p.periodFrom) <= today) ?? periods[0];
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** A period date (ISO "2026-01-01") or a report list date ("01-Jan-2026", the API's display format) as "YYYY-MM-DD". */
export function isoDay(value: unknown): string {
  const text = `${value ?? ''}`.trim();
  const display = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(text);
  if (display) {
    const month = MONTHS.indexOf(display[2].toLowerCase()) + 1;
    return month > 0 ? `${display[3]}-${`${month}`.padStart(2, '0')}-${display[1].padStart(2, '0')}` : text;
  }
  return (toIsoDate(text) ?? text).slice(0, 10);
}
